/**
 * BroadcastAlertOverlay.jsx
 *
 * Intrusive full-screen overlay shown to authenticated non-admin users when
 * they have unread broadcast messages from STARTIP.
 *
 * Rules:
 * - Fetches unread messages from GET /api/me/inbox (polls every 90 s)
 * - Triggers re-check on every route change
 * - Blocks UI entirely — no close button; only "Aceptar" or "Ver más tarde"
 * - "Aceptar" → POST /api/me/inbox/{delivery_id}/read → never shown again
 *   for that campaign (server-side read_at + localStorage cross-tab key)
 * - "Ver más tarde" → hides for THIS page navigation; shows again on next
 *   route change (page-level state, never persisted)
 * - Cross-tab sync: storage event on startip_bcread_* → removes from queue
 * - Never shown for admin users
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Megaphone, Check, Clock, Loader2, ExternalLink } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const POLL_MS = 90_000;
// Routes where the overlay is suppressed (user not yet authenticated)
const SUPPRESSED_ROUTES = ["/login", "/register", "/choose-role", "/google-referral"];

export default function BroadcastAlertOverlay() {
  const { user } = useAuth();
  const location = useLocation();
  const [unread, setUnread]   = useState([]); // all unread non-dismissed broadcasts
  const [snoozedIds, setSnoozedIds] = useState(new Set()); // snoozed for THIS page visit
  const [accepting, setAccepting]   = useState(false);
  const timerRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    if (!user || user.role === "admin") return;
    try {
      const { data } = await axios.get(`${API}/me/inbox`, { withCredentials: true });
      const pending = (data.messages || []).filter(m => {
        if (m.read_at) return false;
        // Cross-tab: already accepted in another open tab
        if (localStorage.getItem(`startip_bcread_${m.delivery_id}`)) return false;
        return true;
      });
      setUnread(pending);
    } catch {
      /* silent — overlay should never break the app */
    }
  }, [user]);

  // Re-check on every route change + start polling
  useEffect(() => {
    // Clear page-level snooze when navigating
    setSnoozedIds(new Set());
    fetchUnread();

    timerRef.current = setInterval(fetchUnread, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchUnread, location.pathname]);

  // Cross-tab sync: if another tab marks a delivery as read, remove it here
  useEffect(() => {
    const handler = (e) => {
      if (e.key?.startsWith("startip_bcread_")) {
        const did = e.key.replace("startip_bcread_", "");
        setUnread(prev => prev.filter(m => m.delivery_id !== did));
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Nothing to render
  const isSuppressed = SUPPRESSED_ROUTES.some(r => location.pathname.startsWith(r));
  const visibleQueue = unread.filter(m => !snoozedIds.has(m.delivery_id));

  if (!user || user.role === "admin" || isSuppressed || visibleQueue.length === 0) {
    return null;
  }

  const msg = visibleQueue[0];

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/me/inbox/${msg.delivery_id}/read`, {}, { withCredentials: true });
      // Optimistic + cross-tab
      localStorage.setItem(`startip_bcread_${msg.delivery_id}`, "1");
      setUnread(prev => prev.filter(m => m.delivery_id !== msg.delivery_id));
      setSnoozedIds(prev => { const s = new Set(prev); s.delete(msg.delivery_id); return s; });
    } catch {
      /* retry next poll */
    } finally {
      setAccepting(false);
    }
  };

  const handleSnooze = () => {
    setSnoozedIds(prev => new Set([...prev, msg.delivery_id]));
  };

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.82)" }}
      data-testid="broadcast-alert-overlay"
    >
      {/* Backdrop blur */}
      <div className="absolute inset-0 backdrop-blur-sm" />

      {/* Card */}
      <div
        className="relative bg-zinc-950 border border-amber-500/40 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
        data-testid="broadcast-alert-card"
      >
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500" />

        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-4 pb-3 border-b border-zinc-800/80">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
            <Megaphone className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-amber-400/80 font-heading font-bold leading-none mb-0.5">
              Aviso oficial
            </p>
            <p className="text-sm font-heading font-black text-white uppercase tracking-wider leading-none">
              STARTIP
            </p>
          </div>
          <span className="text-[11px] text-zinc-500 shrink-0">
            {fmt(msg.delivered_at)}
          </span>
        </div>

        {/* Message body */}
        <div className="px-5 py-4 max-h-56 overflow-y-auto">
          <p className="text-sm text-zinc-200 whitespace-pre-wrap font-body leading-relaxed">
            {linkify(msg.body)}
          </p>
          {msg.deep_link_url && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-cyan-400">
              <ExternalLink className="w-3 h-3 shrink-0" />
              <a
                href={msg.deep_link_url.startsWith("http") ? msg.deep_link_url : undefined}
                onClick={!msg.deep_link_url.startsWith("http") ? undefined : undefined}
                className="underline hover:text-cyan-300 transition-colors truncate"
                target={msg.deep_link_url.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
              >
                {msg.deep_link_url}
              </a>
            </p>
          )}
        </div>

        {/* Footer note */}
        <div className="px-5 pb-1">
          <p className="text-[10px] text-zinc-600 text-center">
            Puedes volver a leer este aviso en{" "}
            <Link to="/inbox" className="text-amber-500/70 hover:text-amber-400 underline" onClick={handleAccept}>
              Avisos de STARTIP
            </Link>{" "}
            de tu perfil.
          </p>
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 pt-3 flex flex-col gap-2">
          <button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 active:scale-[0.98] text-black font-heading font-black text-sm tracking-wide transition-all disabled:opacity-60"
            data-testid="broadcast-alert-accept"
          >
            {accepting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Check className="w-4 h-4" />}
            He leído el aviso
          </button>

          <button
            onClick={handleSnooze}
            className="w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900 text-xs font-body transition-colors"
            data-testid="broadcast-alert-snooze"
          >
            <Clock className="w-3 h-3 shrink-0" />
            Ver más tarde
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function linkify(text) {
  if (!text) return text;
  const URL_RE = /(https?:\/\/[^\s<>]+)/g;
  const parts = text.split(URL_RE);
  return parts.map((p, i) =>
    URL_RE.test(p)
      ? (
        <a
          key={i}
          href={p}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-400 underline hover:text-cyan-300"
          onClick={e => e.stopPropagation()}
        >
          {p}
        </a>
      )
      : p,
  );
}

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      timeZone: "Europe/Madrid",
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
